import React from 'react';
import { BookingTableStyles as S } from './BookingTable.styles';
import Textbox from '@stories/Components/Forms/Textbox/Textbox';
import Select from '@stories/Components/Forms/Select/Select';
import Button from '@stories/Components/Buttons/Button/Button';
import { formatDateLong } from '@helpers/date';
import { MediaComponent } from '@helpers/mediaComponent';
import ApiService from '@core/api/services/ApiService';
import UrlResolver from '@core/url-resolver/UrlResolver';
import ButtonPagination from '@stories/Components/Misc/ButtonPagination/ButtonPagination';
import withWidget from '@hoc/withWidget';
import { HydrateOption } from '@core/enums';
import useMedia from '@hooks/useMedia';
import { Device, until } from '@helpers/media';
const BookingEvent = ({ eventDate }) => {
    return (React.createElement(S.Event, null,
        React.createElement(S.EventInfo, null,
            React.createElement("span", { "data-heading": true }, "Date"),
            React.createElement("span", { "data-date": true },
                React.createElement("span", null, `${formatDateLong(eventDate.startDate, true)}${eventDate.endDate ? ` -` : ''}`),
                React.createElement("span", null, eventDate.endDate && formatDateLong(eventDate.endDate, true)))),
        React.createElement(S.EventInfo, null,
            React.createElement("span", { "data-heading": true }, "Distance"),
            React.createElement("span", null,
                eventDate.distance,
                " mile",
                eventDate.distance === 0 || eventDate.distance > 1 ? 's' : undefined)),
        React.createElement(S.EventInfo, null,
            React.createElement("span", { "data-heading": true }, "Location"),
            React.createElement("span", null, eventDate.location)),
        React.createElement(S.EventInfo, null,
            React.createElement("span", { "data-heading": true }, "Duration"),
            React.createElement("span", null,
                eventDate.duration,
                " day",
                eventDate.duration > 1 ? 's' : undefined)),
        React.createElement(S.EventInfo, { showOrange: eventDate.remainingSpaces === 1 },
            React.createElement("span", { "data-heading": true }, "Remaining Spaces"),
            React.createElement("span", { dangerouslySetInnerHTML: {
                    __html: eventDate.remainingSpaces === 1
                        ? `Only <strong>1</strong> space left`
                        : `${eventDate.remainingSpaces}`,
                } })),
        React.createElement(S.EventPrice, null,
            React.createElement("span", null, eventDate.price),
            React.createElement("span", null, "Per person"),
            React.createElement("span", null, "(Excl VAT)")),
        React.createElement(S.AttendeesAndBookNow, null,
            React.createElement("a", { href: eventDate.bookingUrl, target: "_blank", rel: "noreferrer" },
                React.createElement(Button, { branding: "secondary", title: "Book Now", hoverColour: "primary" })))));
};
const BookingTable = ({ distanceOptions, translations, contactUsLink, courseId, initialData, pageSize, totalPages, }) => {
    const [postcode, setPostcode] = React.useState('');
    const [dateFrom, setDateFrom] = React.useState(null);
    const [dateTo, setDateTo] = React.useState(null);
    const [distance, setDistance] = React.useState(distanceOptions ? distanceOptions[0] : undefined);
    const [response, setResponse] = React.useState({
        result: initialData,
        pageIndex: 1,
        pageSize: pageSize ?? 5,
        status: { message: '', success: true },
        totalPages: totalPages ?? 1,
        validationErrors: [],
    });
    const [errors, setErrors] = React.useState([]);
    const [doneMount, setDoneMount] = React.useState(false);
    const [loading, setLoading] = React.useState();
    const [page, setPage] = React.useState(1);
    const containerRef = React.useRef(null);
    const isMobile = useMedia([until(Device.TabletLarge)], [true], false);
    const filter = async () => {
        setLoading(true);
        const params = {
            postcode,
            distance: distance ? Number(distance?.id) : undefined,
            dateFrom,
            dateTo,
            pageSize: pageSize ?? 5,
            pageIndex: page,
            courseId,
        };
        const response = await ApiService.request({
            baseUrl: UrlResolver.getDomain(),
            method: 'POST',
            slug: 'events',
            controller: 'trainingarea',
            params,
        });
        if (response.validationErrors && response.validationErrors.length > 0) {
            setErrors(response.validationErrors);
        }
        else {
            setErrors([]);
        }
        setResponse(response);
        if (isMobile) {
            containerRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
        setLoading(false);
    };
    const getError = (key) => {
        if (!errors || !key) {
            return undefined;
        }
        const match = errors.find((x) => x.errorSource === key);
        if (match) {
            return match.message;
        }
        return undefined;
    };
    const HelpLink = React.useMemo(() => (React.createElement("a", { href: contactUsLink },
        React.createElement(S.HelpButton, { title: "Contact Us", branding: "secondary", hoverColour: "primary" }))), []);
    const HelpBox = React.useMemo(() => (React.createElement(React.Fragment, null, contactUsLink && (React.createElement(S.HelpContainer, null,
        React.createElement(S.HelpTitle, { size: "small" }, translations['custom.training.cant-find-looking-for']),
        React.createElement(S.HelpBody, { size: "small" }, translations['custom.training.cant-find-looking-for.body']),
        HelpLink)))), []);
    React.useEffect(() => {
        if (doneMount) {
            filter();
        }
        else {
            setDoneMount(true);
        }
    }, [page]);
    return (React.createElement(S.GridOverride, { id: "booking-table", ref: containerRef },
        React.createElement(S.BookingContainer, null,
            React.createElement(S.Title, { semantic: "h1", visual: "h4" }, "Course Booking"),
            React.createElement(S.Small, { size: "small" }, translations['custom.training.vat.message']),
            React.createElement(S.Filters, null,
                React.createElement(S.Filter, null,
                    React.createElement(S.Label, { size: "regular" }, "Postcode:"),
                    React.createElement(Textbox, { value: postcode, onChange: (e) => setPostcode(e.currentTarget.value), placeholder: "Enter your Postcode" }),
                    getError('postcode') && React.createElement(S.ValidationError, null, getError('postcode'))),
                distance && (React.createElement(S.Filter, null,
                    React.createElement(S.Label, { size: "regular" }, "Distance:"),
                    React.createElement(S.SelectWrapper, null,
                        React.createElement(Select, { options: distanceOptions, selectedOption: distance?.value, onClick: (i) => setDistance(distanceOptions[i]) })),
                    getError('distance') && (React.createElement(S.ValidationError, null, getError('distance'))))),
                React.createElement(S.Filter, null,
                    React.createElement(S.Label, { size: "regular" }, "Date Range:"),
                    React.createElement(S.Dates, null,
                        React.createElement(S.DateLayout, null,
                            React.createElement(S.DateInput, { type: "date", value: dateFrom ? dateFrom?.toISOString().substring(0, 10) : '', onChange: (ev) => setDateFrom(ev.currentTarget.valueAsDate) }),
                            React.createElement("span", null, "to"),
                            React.createElement(S.DateInput, { type: "date", value: dateTo ? dateTo?.toISOString().substring(0, 10) : '', onChange: (ev) => setDateTo(ev.currentTarget.valueAsDate) })),
                        (dateFrom || dateTo) && (React.createElement(S.ClearDateRange, { onClick: () => {
                                setDateFrom(null);
                                setDateTo(null);
                            } }, "Clear range?")),
                        getError('dates') && React.createElement(S.ValidationError, null, getError('dates'))))),
            React.createElement(Button, { title: "Search", branding: "primaryAlt", onClick: () => {
                    setPage(1);
                    filter();
                }, disabled: loading }),
            React.createElement(MediaComponent, { desktop: true }, HelpBox)),
        React.createElement(S.ListingContainer, null,
            response.result.map((event, i) => (React.createElement(BookingEvent, { key: `BookingTable_${event.courseId}_${i}`, eventDate: event }))),
            (response.result.length === 0 || initialData.length === 0) && !loading && (React.createElement(S.NoResultsWrapper, null,
                React.createElement(S.NoResults, { size: "regular" }, translations['custom.training.no-results']),
                HelpLink)),
            (response?.totalPages > 0 || initialData.length > 0) && (React.createElement(S.PaginationWrapper, null,
                React.createElement(ButtonPagination, { page: page, onPageChange: (p) => {
                        setPage(p);
                    }, totalPages: response ? response.totalPages : 0, disabled: loading })))),
        React.createElement(MediaComponent, { mobile: true }, HelpBox)));
};
export default withWidget(BookingTable, 'BookingTable', { hydrate: HydrateOption.InView });
